import Header from "../../components/Header"
import { Box, Tab } from "@mui/material";
import { TabContext, TabPanel, TabList } from "@mui/lab";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../../api";
import DeviceSettings from "./lecsManagement-components/DeviceSettings";
import DetectionResponse from "./lecsManagement-components/DetectionResponse";
import NotificationsSettings from "./lecsManagement-components/NotificationsSettings";
import IntegrationsPanel from "./lecsManagement-components/IntegrationsPanel";

const LecsManagement = () => {
  const location = useLocation();
  // Deep-link support: /lecs-management?tab=2&section=ot_anomalies
  // Used by the OT Security page's "Configure this event" / "Configure severity rules" shortcuts.
  // tab/section are harmless to read from the URL (they just pick a UI panel, nothing opens
  // automatically from them alone). The per-event payload is NOT in the URL — see anomalyPrefill.
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get("tab") || "1");
  const [section] = useState(() => new URLSearchParams(location.search).get("section") || undefined);

  // Read via a sessionStorage "mailbox" written by the OT Security page's context menu, not a
  // URL query param — a URL persists (bookmarks, browser history, back/forward, other nav links
  // reusing the current search) and kept re-triggering this prefill on plain revisits to this
  // page. sessionStorage is read AND deleted in this same initializer, so it is consumed exactly
  // once per click, however many times this component itself re-mounts afterwards.
  const [anomalyPrefill, setAnomalyPrefill] = useState(() => {
    try {
      const raw = sessionStorage.getItem("ot-anomaly-prefill");
      if (!raw) return null;
      sessionStorage.removeItem("ot-anomaly-prefill");
      const payload = JSON.parse(raw);
      if (payload.signature_id === undefined) return null;
      return {
        signature_id: Number(payload.signature_id),
        proto: payload.proto || "",
        src: payload.src || "",
        dst: payload.dst || "",
        severity: (payload.severity || "").toLowerCase(),
      };
    } catch (_) {
      return null;
    }
  });

  const [rules, setRules] = useState([]);
  const [whiteList, setWhiteList] = useState({});
  const [probe, setProbe] = useState(null);
  const [syslog, setSyslog] = useState(null);
  const [offline, setOffline] = useState({});
  const [otSettings, setOtSettings] = useState(null);
  const [systemOtSettings, setSystemOtSettings] = useState(null);
  const [severityRules, setSeverityRules] = useState([]);
  const [severityCatalog, setSeverityCatalog] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [otEnabled, setOtEnabled] = useState(true);
  const [customRulesEnabled, setCustomRulesEnabled] = useState(false);
  useEffect(() => {
    API.getLicense().then((res) => {
      const modules = res.data?.modules ?? [];
      const ot = modules.find(m => m.name === 'ot');
      setOtEnabled(ot ? ot.enabled : false);
      const cr = modules.find(m => m.name === 'blacklist');
      setCustomRulesEnabled(cr ? cr.enabled : false);
    }).catch((err) => {
      console.error('[LecsManagement] getLicense failed:', err);
      setOtEnabled(false);
    });
    API.getNotifications().then((res) => {
      setNotifications(res.data ?? null);
    }).catch((err) => {
      console.error('[LecsManagement] getNotifications failed:', err);
    });
    API.getConfig().then((res) => {
      const configs = res.data;
      setWhiteList(configs?.find((config) => config.type === "whitelist")?.config ?? {});
      setProbe(configs?.find((config) => config.type === "probe")?.config ?? false);
      setSyslog(configs?.find((config) => config.type === "syslog")?.config ?? {});
      setRules(configs?.find((config) => config.type === "rules")?.config ?? []);
      setOffline(configs?.find((config) => config.type === "offline_notify")?.config ?? {});
      setOtSettings(configs?.find((config) => config.type === "ot_settings")?.config ?? null);
      setSeverityRules(configs?.find((config) => config.type === "severity_rules")?.config ?? []);
    }).catch((err) => {
      console.error("[OtSettings] getConfig failed:", err?.response?.status ?? err?.message);
    });
    API.getSystemConfig("ot", "severity_rules_catalog").then((res) => {
      setSeverityCatalog(res.data?.[0]?.value ?? null);
    }).catch((err) => {
      console.error("[OtSettings] getSystemConfig(severity_rules_catalog) failed:", err?.response?.status ?? err?.message);
    });
    API.getSystemConfig("ot", "supported_protocols").then((res) => {
      const list = res.data?.[0]?.value ?? [];
      if (!list.length) return;

      // zeek_proto → key used in OT Settings protocols_enabled
      const ZEEK_TO_OT_KEY = {
        "s7":            "s7tcp",
        "enip-cip":      "enip_cip",
        "dnp3-sa":       "dnp3_sa",
        "opcua-pubsub":  "opcua_pubsub",
        "ge-srtp":       "ge",
        "hart-ip":       "hart_ip",
        "mms":           "iec_mms",
        "c37.118":       "c37118",
        "iccp-tase2":    "iccp",
        "schneider-umas":"umas",
        "sparkplug-b":   "sparkplug",
        "dds-rtps":      "dds",
        "ur-rtde":       "ur",
        "modbus-tls":    "modbus_tls",
        "ws-discovery":  "ws_discovery",
      };

      // Backend's PORT_FIELD_KEYS (server/utils/otConfigValidation.mjs) rejects the
      // WHOLE payload if `ports` carries even one unrecognized key — these protocols
      // are native/fixed-port Zeek analyzers with no configurable port set (RTP is
      // negotiated dynamically, GOOSE/SV run on L2 EtherType, the rest are analyzers
      // on a single hardcoded port), so no `_ports` entry must ever be synthesized
      // for them, no matter what the system catalog reports as a "default port".
      const NO_PORT_CONFIG = new Set(["rtp", "snmp", "ssh", "rdp", "telnet", "tftp", "goose", "sv"]);

      const protocols_enabled = {};
      const ports = {};
      const protocol_labels = {};

      list.forEach((proto) => {
        const rawKey = proto.zeek_proto;
        if (!rawKey) return;
        const key = ZEEK_TO_OT_KEY[rawKey] ?? rawKey;

        protocols_enabled[key] = false;
        protocol_labels[key] = proto.label;

        if (!NO_PORT_CONFIG.has(key) && Array.isArray(proto.default_ports) && proto.default_ports.length > 0) {
          // The system protocol catalog carries no tcp/udp info per port (just
          // {port, group}) — the backend's isValidPortEntry requires a "/tcp"
          // or "/udp" suffix on every entry, so a bare port number fails
          // validation on save. Default to tcp (the common case for most of
          // these protocols); the user can still correct a specific entry to
          // "/udp" in the ports text field before saving.
          ports[`${key}_ports`] = proto.default_ports
            .map((p) => (p?.port != null ? `${p.port}/tcp` : null))
            .filter(Boolean);
        }
      });

      setSystemOtSettings({ protocols_enabled, ports, protocol_labels });
    }).catch((err) => {
      console.error("[OtSettings] getSystemConfig failed:", err?.response?.status ?? err?.message);
    });
  }, []);

  return (
    <Box m={"20px"}>
      <Header title={"LECS MANAGEMENT"} />
      <TabContext value={tab}>
        <TabList
          variant="fullWidth"
          onChange={(e, newValue) => setTab(newValue)}
          aria-label="s"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={tabStyle.tabList}
        >
          <Tab 
            value={"1"}
            sx={tabStyle.tab}
            className="tabIp"
            label="Detection & Response"
          />
          <Tab 
            value={"2"}
            sx={tabStyle.tab}
            className="tabIp"
            label="Device Settings"
          />
          <Tab
            value={"3"}
            sx={tabStyle.tab}
            className="tabIp"
            label="Notifications"
          />
          <Tab
            value={"4"}
            sx={tabStyle.tab}
            className="tabIp"
            label="Integrations"
          />
        </TabList>
        <TabPanel value={"1"}>
          {/* <ResponseSupp 
            whiteList={whiteList}
            setWhiteList={setWhiteList}
          /> */}
          <DetectionResponse
            whitelist={whiteList}
            setWhitelist={setWhiteList}
            rules={rules}
            setRules={setRules}
            probeModeConfig={probe}
            setProbeModeConfig={setProbe}
            customRulesEnabled={customRulesEnabled}
          />
        </TabPanel>
        <TabPanel value={"2"}>
          <DeviceSettings
            offlineNotify={offline}
            setOfflineNotify={setOffline}
            storedOtSettings={otSettings}
            systemOtSettings={systemOtSettings}
            severityRules={severityRules}
            setSeverityRules={setSeverityRules}
            severityCatalog={severityCatalog}
            anomalyPrefill={anomalyPrefill}
            onAnomalyPrefillConsumed={() => setAnomalyPrefill(null)}
            section={section}
            otEnabled={otEnabled}
          />
        </TabPanel>
        <TabPanel value={"3"}>
          <NotificationsSettings notifications={notifications} setNotifications={setNotifications} />
        </TabPanel>
        <TabPanel value={"4"}>
          <IntegrationsPanel storedSyslog={syslog} />
        </TabPanel>
      </TabContext>
    </Box>
  )
};

export default LecsManagement;

const tabStyle = {
  tab: {
    color: "#eaeaea",
    fontWeight: 600,
    fontFamily: "Poppins"
  },
  tabList: {
    backgroundColor: "#1E1E1E",
    display: "flex",
    width: "100%",
    borderRadius: 1,
    "& .MuiTabs-indicator": {
      backgroundColor: "#459296",
    },
    "& .MuiTabs-scrollButtons": {
      color: "white",
    }
  }
}